
import * as React from "react"

// Multiple breakpoint definitions for improved responsiveness
const BREAKPOINTS = {
  xs: 480,    // Extra small devices
  sm: 640,    // Small devices
  md: 768,    // Medium devices (original mobile breakpoint)
  lg: 1024,   // Large devices
  xl: 1280    // Extra large devices
}

// Create context to make responsive information available throughout the app
const ResponsiveContext = React.createContext<{
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  screenSize: string;
  orientation: 'portrait' | 'landscape' | undefined;
}>({
  isMobile: false,
  isTablet: false,
  isDesktop: true,
  screenSize: 'unknown',
  orientation: undefined
});

/**
 * Hook that checks if the current device is a mobile device
 * based on screen width (primary) and userAgent (secondary validation)
 */
export function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState<boolean | undefined>(undefined)

  React.useEffect(() => {
    // Helper function to determine if device is mobile based on screen width
    const checkIfMobile = () => {
      const width = window.innerWidth;
      return width < BREAKPOINTS.md;
    }

    // Initial check on mount using media query
    const mql = window.matchMedia(`(max-width: ${BREAKPOINTS.md - 1}px)`)
    
    // Update handler that uses both methods for detection
    const onChange = () => {
      const isMobileByWidth = checkIfMobile();
      
      // Optimize renders by only updating state when there's an actual change
      setIsMobile(isMobileByWidth);
      
      // Add some basic logging for debugging
      console.debug(`Screen width: ${window.innerWidth}px, isMobile: ${isMobileByWidth}`);
    }
    
    // Add event listener for screen size changes
    mql.addEventListener("change", onChange)
    
    // Set initial value
    onChange();
    
    // Clean up event listener on unmount
    return () => mql.removeEventListener("change", onChange)
  }, [])

  // Default to desktop if still undefined (this ensures a non-null value is always returned)
  return isMobile ?? false;
}

/**
 * Enhanced hook that provides more detailed responsive information
 */
export function useResponsive() {
  const [state, setState] = React.useState({
    isMobile: false,
    isTablet: false,
    isDesktop: true,
    screenSize: 'unknown',
    orientation: undefined as 'portrait' | 'landscape' | undefined
  });

  React.useEffect(() => {
    // Helper function to determine current screen size category
    const getScreenSize = () => {
      const width = window.innerWidth;
      
      if (width < BREAKPOINTS.sm) return 'xs';
      if (width < BREAKPOINTS.md) return 'sm';
      if (width < BREAKPOINTS.lg) return 'md';
      if (width < BREAKPOINTS.xl) return 'lg';
      return 'xl';
    };

    // Helper to determine device orientation
    const getOrientation = () => {
      if (typeof window.orientation !== 'undefined') {
        // For older browsers that support window.orientation
        return Math.abs(Number(window.orientation)) === 90 ? 'landscape' : 'portrait';
      } else if (window.matchMedia) {
        // For modern browsers
        return window.matchMedia('(orientation: portrait)').matches ? 'portrait' : 'landscape';
      }
      return undefined;
    };

    // Update all responsive state
    const updateResponsiveState = () => {
      const width = window.innerWidth;
      const screenSize = getScreenSize();
      
      setState({
        isMobile: width < BREAKPOINTS.md,
        isTablet: width >= BREAKPOINTS.md && width < BREAKPOINTS.lg,
        isDesktop: width >= BREAKPOINTS.lg,
        screenSize,
        orientation: getOrientation()
      });
    };

    // Set up event listeners for both resize and orientation change
    window.addEventListener('resize', updateResponsiveState);
    window.addEventListener('orientationchange', updateResponsiveState);
    
    // Initialize state
    updateResponsiveState();
    
    // Clean up event listeners
    return () => {
      window.removeEventListener('resize', updateResponsiveState);
      window.removeEventListener('orientationchange', updateResponsiveState);
    };
  }, []);

  return state;
}

/**
 * Provider component that makes responsive context available to its children
 */
export function ResponsiveProvider({ children }: { children: React.ReactNode }) {
  const responsiveState = useResponsive();
  
  return (
    <ResponsiveContext.Provider value={responsiveState}>
      {children}
    </ResponsiveContext.Provider>
  );
}

// Export context hook for easy access
export const useResponsiveContext = () => React.useContext(ResponsiveContext);
